import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { SavedSearchScope } from '../types/savedSearch'

function getUserIdSync(): number | null {
  try {
    const token = localStorage.getItem('stonks_token')
    if (!token) return null
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (payload.exp && payload.exp * 1000 < Date.now()) return null
    return typeof payload.id === 'number' ? payload.id : null
  } catch { return null }
}
import {
  useSavedSearches,
  useCreateSavedSearch,
  useUpdateSavedSearch,
  useDeleteSavedSearch,
} from './useSavedSearches'

interface UsePageTabsOptions<F> {
  scope: SavedSearchScope
  filters: F
  setFilters: (f: F) => void
  setSearch?: (s: string) => void
  defaultFilters: F
  /** Strip fields that shouldn't be persisted (e.g. one-off score filters) */
  sanitize?: (f: F) => F
}

export function usePageTabs<F>({
  scope,
  filters,
  setFilters,
  setSearch,
  defaultFilters,
  sanitize,
}: UsePageTabsOptions<F>) {
  const safe = useCallback((f: F) => (sanitize ? sanitize(f) : f), [sanitize])

  const { data: savedSearchesData } = useSavedSearches<F>(scope)
  const createSearch = useCreateSavedSearch<F>(scope)
  const updateSearch = useUpdateSavedSearch<F>()
  const deleteSearch = useDeleteSavedSearch(scope)

  const savedSearches = savedSearchesData?.searches ?? []

  // Scope the active-tab key per user so different accounts on the same browser
  // don't bleed into each other. Read user ID synchronously from the JWT to
  // avoid a hydration race with auth state.
  const tabKey = useMemo(() => {
    const uid = getUserIdSync()
    return uid ? `stonks_tabs_active:${uid}:${scope}` : `stonks_tabs_active:${scope}`
  }, [scope])

  const [activeTabId, setActiveTabId] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem(tabKey)
      return saved ? parseInt(saved) : null
    } catch { return null }
  })

  // Prevent auto-save firing on mount or tab switch
  const suppressAutoSave = useRef(true)

  // Persist active tab id
  useEffect(() => {
    try {
      if (activeTabId !== null) {
        localStorage.setItem(tabKey, activeTabId.toString())
      } else {
        localStorage.removeItem(tabKey)
      }
    } catch { /* ignore */ }
  }, [activeTabId, tabKey])

  // Auto-save active tab whenever filters change
  useEffect(() => {
    if (activeTabId === null) return
    if (suppressAutoSave.current) {
      suppressAutoSave.current = false
      return
    }
    const timeout = setTimeout(() => {
      updateSearch.mutate({ id: activeTabId, updates: { filters: safe(filters) } })
    }, 500)
    return () => clearTimeout(timeout)
  }, [filters, activeTabId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabClick = useCallback((tabId: number | null) => {
    setActiveTabId(tabId)
    suppressAutoSave.current = true
    if (tabId === null) {
      setFilters(defaultFilters)
      setSearch?.('')
    } else {
      const saved = savedSearches.find(s => s.id === tabId)
      if (saved) {
        setFilters(saved.filters)
        setSearch?.((saved.filters as any).q ?? '')
      }
    }
  }, [savedSearches, defaultFilters, setFilters, setSearch])

  const handleCreateTab = useCallback((name: string, onCreated?: (id: number) => void) => {
    if (!name.trim()) return
    createSearch.mutate(
      { name: name.trim(), filters: safe(filters) },
      { onSuccess: (data) => { setActiveTabId(data.search.id); onCreated?.(data.search.id) } }
    )
  }, [createSearch, filters, safe])

  const handleDeleteTab = useCallback((tabId: number) => {
    deleteSearch.mutate(tabId)
    if (activeTabId === tabId) {
      setActiveTabId(null)
      suppressAutoSave.current = true
      setFilters(defaultFilters)
      setSearch?.('')
    }
  }, [deleteSearch, activeTabId, defaultFilters, setFilters, setSearch])

  return {
    savedSearches,
    activeTabId,
    handleTabClick,
    handleCreateTab,
    handleDeleteTab,
    isCreating: createSearch.isPending,
  }
}
